import React from 'react';
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import {formatCell, isSuppressedCell} from './dashboardCards';
import {SuppressedValue} from './SuppressedValue.comp';
import {BAND_COLOR, RISK_BAND_COLORS, READINESS_BAND_COLORS, CONTEXT_BAND_COLORS} from '../../utils/constants';
import {BandChip} from './widgets.comp';
import {useTableControls} from './useTableControls';
import {SortFilterHeaderCell} from './SortFilterHeaderCell.comp';
import {TableToolbar} from './TableToolbar.comp';

const NAVY = '#052F44';
const TEAL = '#1B6B55';

const headCell = {backgroundColor: NAVY, color: '#fff', fontSize: '13px', fontWeight: 700, borderBottom: 'none', py: 1.75, px: 2, whiteSpace: 'nowrap'};
const bodyCell = {fontSize: '13px', color: '#374151', py: 1.6, px: 2, borderBottom: '1px solid #F3F4F6'};

// Chip colour for a badge / status cell: the shared rubric bands, plus the
// learning labels (Strong / Moderate signal, Pass / Not Pass, Deployed …).
const CHIP_COLORS = {
    Strong: TEAL, 'Strong Signal': TEAL, Moderate: '#EFA020', 'Moderate Signal': '#EFA020', Early: '#6A7282', Weak: '#E04535', 'Weak Signal': '#E04535',
    Pass: TEAL, Passed: TEAL, 'Not Pass': '#E8730C', 'Not passed': '#E8730C', 'Not Passed': '#E8730C',
    Deployed: TEAL, Active: TEAL, Completed: TEAL, Created: '#6A7282', Met: TEAL, 'Below 50%': '#1F2937',
};
const chipColor = (v) => CHIP_COLORS[v] || RISK_BAND_COLORS[v] || READINESS_BAND_COLORS[v] || CONTEXT_BAND_COLORS[v] || BAND_COLOR[v] || '#6B7280';

const BAND_TYPES = new Set(['badge', 'status', 'rubric']);
const present = (v) => v !== null && v !== undefined && v !== '' && v !== '—';
const numeric = (v) => Number.isFinite(Number(typeof v === 'string' ? v.replace(/%\s*$/, '').replace(/,/g, '') : v));
// The sort/filter meta of a card column: the datahouse marks percents and badges; a column of numbers sorts as numbers.
const columnMeta = (c) => {
    const vals = (c.values || []).filter(present);
    const type = c.column_type === 'percent' ? 'percent'
        : BAND_TYPES.has(c.column_type) ? 'band'
            : (vals.length > 0 && vals.every(numeric) ? 'number' : 'text');
    return {key: c.column_name, label: c.column_name, type, accessor: (r) => (isSuppressedCell(c.cell_data?.[r._i]) ? null : c.values?.[r._i])};
};

/**
 * A TableCard drawn exactly as the data sends it: one column per
 * table_objects entry, in order, with its own header. badge / status columns
 * render as chips, percent columns get a "%". Used where the Data Map lets the
 * card decide its columns, so a column the datahouse adds or renames shows up
 * without a code change. Every column sorts and (with enough rows) filters
 * from its header; `controls={false}` draws the plain table.
 */
const CardTable = ({card, emptyText = 'No data yet.', minWidth = 900, hide = [], controls = true}) => {
    const hidden = hide.map((h) => h.toLowerCase());
    const cols = (card?.table_objects || []).filter((c) => !hidden.includes(String(c.column_name || '').toLowerCase()));
    const rowCount = Math.max(0, ...cols.map((c) => (c.values || []).length));
    const rows = React.useMemo(() => Array.from({length: rowCount}, (_, i) => ({_i: i})), [rowCount]);
    const columns = cols.map(columnMeta);
    const t = useTableControls(rows, columns);
    const shown = controls ? t.rows : rows;
    return (
        <>
            {controls && (t.chips.length > 0 || rowCount >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', backgroundColor: 'var(--brand-surface, #fff)',
            }}>
                <Table sx={{minWidth}}>
                    <TableHead>
                        <TableRow>
                            {cols.map((c, j) => {
                                const sx = {...headCell, textAlign: j === 0 ? 'left' : 'center', pl: j === 0 ? 2.5 : 2};
                                return controls
                                    ? <SortFilterHeaderCell key={c.column_name || j} {...t.headerProps(c.column_name)} sx={sx}>{c.column_name}</SortFilterHeaderCell>
                                    : <TableCell key={c.column_name || j} sx={sx}>{c.column_name}</TableCell>;
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowCount === 0 && (
                            <TableRow>
                                <TableCell colSpan={Math.max(cols.length, 1)} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{emptyText}</TableCell>
                            </TableRow>
                        )}
                        {rowCount > 0 && shown.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={Math.max(cols.length, 1)} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>No row matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {shown.map((r, k) => {
                            const i = r._i;
                            return (
                                <TableRow key={i} sx={{backgroundColor: k % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                    {cols.map((c, j) => {
                                        // A cell under the n floor (cell_data.suppressed) shows the placeholder, never its value.
                                        const sup = isSuppressedCell(c.cell_data?.[i]);
                                        const text = sup ? '—' : formatCell(c, c.values?.[i]);
                                        const isChip = !sup && (c.column_type === 'badge' || c.column_type === 'status') && text !== '—';
                                        return (
                                            <TableCell key={c.column_name || j} sx={{
                                                ...bodyCell, textAlign: j === 0 ? 'left' : 'center', pl: j === 0 ? 2.5 : 2,
                                                ...(j === 0 ? {fontWeight: 700, color: NAVY} : {}),
                                            }}>
                                                {sup ? <SuppressedValue variant="cell"/> : (isChip ? <BandChip label={text} color={chipColor(text)}/> : text)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default CardTable;
